import React, { useState, useEffect, useCallback } from 'react';
import './LoginPage.css';
import logo from '../assets/logo.png';
import RegisterModal from './RegisterModal';
import ForgotPasswordModal from './ForgotPasswordModal';

const API_BASE = process.env.REACT_APP_API_BASE;

export default function LoginPage({ onLoginSuccess }) {
  const [accountType, setAccountType] = useState('admin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegister, setShowRegister]       = useState(false);
  const [showForgot, setShowForgot]           = useState(false);

  const loadCaptcha = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/captcha`);
      const data = await res.json();
      setCaptchaToken(data.token);
      setCaptchaImage(data.image_base64);
      setCaptchaCode('');
    } catch {
      // silently fail — captcha shows placeholder
    }
  }, []);

  useEffect(() => { loadCaptcha(); }, [loadCaptcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaCode.trim()) {
      setError('Please enter the security verification code.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username.trim(),
          password,
          user_type: accountType,
          captcha_code: captchaCode,
          captcha_token: captchaToken,
          remember_me: rememberMe,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Login failed. Please check your credentials.');
        loadCaptcha();
        return;
      }
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('auth_token', data.access_token);
      storage.setItem('user', JSON.stringify(data.user));
      onLoginSuccess(data.user);
    } catch {
      setError('Connection failed. Please check your network and try again.');
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const displayLabel = username
    ? username.replace(/@.*/, '').toUpperCase().slice(0, 12)
    : 'GUEST';

  return (
    <div className="lp-wrapper">
      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onLoginRedirect={() => setShowRegister(false)}
        />
      )}
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
      <div className="lp-card-outer">

      {/* ── Left panel ── */}
      <div className="lp-left">
        <div className="lp-brand">
          <img src={logo} alt="Logo" className="lp-logo-img" />
        </div>

        <div className="lp-hero">
          <h1 className="lp-hero-title">
            Elevating<br />
            <span className="lp-hero-red">Hospitality</span><br />
            Management.
          </h1>
          <p className="lp-hero-sub">
            Access your curated canvas of properties, insights, and guest experiences.
          </p>
        </div>

        <div className="lp-stats-card">
          <div className="lp-stat">
            <span className="lp-stat-num">200+</span>
            <span className="lp-stat-label">Properties</span>
          </div>
          <div className="lp-stat-divider" />
          <div className="lp-stat">
            <span className="lp-stat-num">12K+</span>
            <span className="lp-stat-label">Managers</span>
          </div>
          <div className="lp-stat-divider" />
          <div className="lp-stat">
            <span className="lp-stat-num">98%</span>
            <span className="lp-stat-label">Satisfaction</span>
          </div>
        </div>

        <div className="lp-social-proof">
          <div className="lp-avatars">
            <div className="lp-av lp-av-1">S</div>
            <div className="lp-av lp-av-2">K</div>
            <div className="lp-av lp-av-3">M</div>
          </div>
          <span>Trusted by over 12,000 managers worldwide.</span>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="lp-right">
        <div className="lp-card">

          {/* Logo + username label */}
          <div className="lp-user-section">
            <img src={logo} alt="Logo" className="lp-user-avatar" />
            <span className="lp-user-label">{displayLabel}</span>
          </div>

          <h2 className="lp-title">Welcome Back</h2>
          <p className="lp-subtitle">
            Please enter your credentials to manage your portfolio.
          </p>

          <form onSubmit={handleSubmit} noValidate>

            {/* Account type */}
            <div className="lp-field">
              <label className="lp-label">ACCOUNT TYPE</label>
              <div className="lp-toggle">
                <button
                  type="button"
                  className={`lp-toggle-btn ${accountType === 'admin' ? 'active' : ''}`}
                  onClick={() => setAccountType('admin')}
                >
                  <span className="lp-dots">••</span> Admin
                </button>
                <button
                  type="button"
                  className={`lp-toggle-btn ${accountType === 'property_manager' ? 'active' : ''}`}
                  onClick={() => setAccountType('property_manager')}
                >
                  <span className="lp-pm-icon">🏠</span> Property Manager
                </button>
              </div>
            </div>

            {/* Username / Email */}
            <div className="lp-field">
              <label className="lp-label">USERNAME / EMAIL</label>
              <div className="lp-input-wrap">
                <svg className="lp-icon" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="7" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M3 18c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                  type="text"
                  className="lp-input"
                  placeholder={accountType === 'admin' ? 'administrator@airbnb.admin' : 'manager@example.com'}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password */}
            <div className="lp-field">
              <div className="lp-label-row">
                <label className="lp-label">PASSWORD</label>
                <button type="button" className="lp-forgot" onClick={() => setShowForgot(true)}>Forgot?</button>
              </div>
              <div className="lp-input-wrap">
                <svg className="lp-icon" viewBox="0 0 20 20" fill="none">
                  <rect x="4" y="9" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M7 9V6a3 3 0 116 0v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="lp-input"
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="lp-eye"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
                      <path d="M3 3l14 14M12.45 12.45A3 3 0 017.55 7.55M8.7 5.1A8.7 8.7 0 0110 5c5 0 8 5 8 5a15.3 15.3 0 01-2.7 3.3M6.5 6.5A14.5 14.5 0 002 10s3 5 8 5a8.6 8.6 0 004.5-1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
                      <path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" stroke="currentColor" strokeWidth="1.5"/>
                      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* CAPTCHA */}
            <div className="lp-field">
              <label className="lp-label">SECURITY VERIFICATION</label>
              <div className="lp-captcha-row">
                <div className="lp-captcha-box">
                  <div className="lp-captcha-img" onClick={loadCaptcha} title="Click to refresh">
                    {captchaImage ? (
                      <img src={`data:image/png;base64,${captchaImage}`} alt="Security code" />
                    ) : (
                      <span className="lp-captcha-placeholder">Loading…</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="lp-captcha-refresh"
                    onClick={(e) => { e.stopPropagation(); loadCaptcha(); }}
                    title="Refresh captcha"
                  >↻ Refresh</button>
                </div>
                <div className="lp-input-wrap lp-captcha-input">
                  <svg className="lp-icon" viewBox="0 0 20 20" fill="none">
                    <path d="M10 2l6 2.5v5c0 3.5-2.5 6.5-6 7.5C4.5 16 2 13 2 9.5v-5L10 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                  </svg>
                  <input
                    type="text"
                    className="lp-input"
                    placeholder="Enter code"
                    value={captchaCode}
                    onChange={(e) => setCaptchaCode(e.target.value)}
                    maxLength={6}
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>

            {/* Remember me */}
            <label className="lp-remember">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>Remember this session for 30 days</span>
            </label>

            {error && <div className="lp-error">{error}</div>}

            <button type="submit" className="lp-submit" disabled={loading}>
              {loading
                ? <span className="lp-spinner" />
                : (
                  <>
                    Sign In to Portal
                    <svg viewBox="0 0 20 20" fill="none" width="18" height="18">
                      <path d="M4 10h12M12 6l4 4-4 4" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </>
                )
              }
            </button>
          </form>

          <div className="lp-card-footer">
            <p>New to the platform?</p>
            <button type="button" className="lp-request" onClick={() => setShowRegister(true)}>Create Account</button>
          </div>
        </div>

        <footer className="lp-page-footer">
          <span>© 2024 AIRBNB ADMIN INC.</span>
          <span className="lp-sep">•</span>
          <span>SYSTEM STATUS</span>
          <span className="lp-sep">•</span>
          <span>SECURITY POLICY</span>
          <span className="lp-sep">•</span>
          <span>API DOCUMENTATION</span>
        </footer>
      </div>
      </div>
    </div>
  );
}
